import React from 'react';
import { Form, MultiSelect, Button } from '@loanpal/lumos__core';
import { object, string, array } from "yup";
import { Box } from "theme-ui";
import {
  Text
} from "@aws-amplify/ui-react";

function parseOfferIds(offersArray) {
  if (offersArray == null || offersArray === "") return [];
  if (Array.isArray(offersArray)) {
    return offersArray.map((v) => String(v).trim()).filter(Boolean);
  }
  const raw = String(offersArray).trim();
  // Tolerate accidental JSON array strings from imports/tools
  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v).trim()).filter(Boolean);
      }
    } catch {
      // fall through to CSV split
    }
  }
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

const Offers = ({ active, offerItems, offersArray = [], setInput, addSnack, readOnly, channelOfferIds }) => {
  const schema = object({
    offers: array().of(string()).min(1, "Select at least 1 offer")
  });

  const selectedIds = parseOfferIds(offersArray);

  const initialValues = {
    offers: selectedIds
  };

  const offerValueToId = (v) => {
    if (v == null) return "";
    if (typeof v === "string" || typeof v === "number") return String(v).trim();
    if (typeof v === "object") {
      if (v.value != null) return offerValueToId(v.value);
      if (v.id != null) return String(v.id).trim();
    }
    return "";
  };

  const handleSubmit = ({...values}) => {
    const ids = (values.offers || []).map(offerValueToId).filter(Boolean);
    setInput("offer", ids.join(","));
    addSnack("success", "Offers");
  }

  const buildOfferSelectItems = () => {
    const rows = (offerItems ?? []).map((offer) => {
      const id = offerValueToId(offer.value);
      const inChannel =
        channelOfferIds != null && channelOfferIds.has(id);
      return { offer, inChannel, id };
    });

    // Ensure every currently selected id has a MultiSelect item, even if the
    // catalog fetch missed it (otherwise the control can look empty).
    const present = new Set(rows.map((r) => r.id).filter(Boolean));
    selectedIds.forEach((id) => {
      if (!id || present.has(id)) return;
      rows.push({
        id,
        inChannel: channelOfferIds != null && channelOfferIds.has(id),
        offer: {
          name: `${id} (saved selection)`,
          value: id,
        },
      });
      present.add(id);
    });

    if (channelOfferIds != null) {
      rows.sort((a, b) => {
        if (a.inChannel === b.inChannel) return 0;
        return a.inChannel ? -1 : 1;
      });
    }

    // Use plain strings (not React nodes) so MultiSelect search works.
    return rows.map(({ offer, inChannel }) => ({
      name: inChannel ? `✓ ${offer.name}` : String(offer.name ?? ""),
      value: offer.value,
    }));
  };

  const items = buildOfferSelectItems();
  // Remount when catalog or selection changes so MultiSelect picks up items + initialValues.
  const formKey = `${selectedIds.join("|")}::${items.length}`;

  const OffersInput = () => {
    return (
      <>
      <Box sx={{ width: "100%", mr: 5, mt: 2 }}>
      <MultiSelect
          name="offers"
          label={<Text>
          Offer(s) Requested
          <Text as="span" fontSize="0.8rem" color="#F1C232">
              {' '}
          (optional)
          </Text>
          </Text>}
          description="Select offers and click Confirm. ✓ = in the channel above. Already-selected offers are hidden from the open list until you remove them."
          items={items}
          readOnly={readOnly}
      />
      </Box>
      <Button disabled={readOnly} type="submit">Confirm Selected Offers</Button>
      </>
    )
  }

  return (
    <>
    {active &&
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          validationSchema={schema}
          initialValues={initialValues}>
            <OffersInput />
        </Form>
    }
    </>
  )
}

export default Offers;
